import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import {
  getEmailConfigurations,
  updateEmailConfiguration,
  EmailConfiguration,
  eventTypeLabels
} from '../api/emailConfigService';

const EmailConfig: React.FC = () => {
  const queryClient = useQueryClient();
  const [editingConfig, setEditingConfig] = useState<EmailConfiguration | null>(null);
  const [recipientInput, setRecipientInput] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);

  // Fetch email configurations
  const { data: configurations, isLoading } = useQuery({
    queryKey: ['emailConfigurations'],
    queryFn: getEmailConfigurations,
  });

  // Update email configuration mutation
  const updateConfigMutation = useMutation({
    mutationFn: ({ eventType, data }: { eventType: string; data: { recipients?: string[]; enabled?: boolean } }) =>
      updateEmailConfiguration(eventType, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailConfigurations'] });
      setEditingConfig(null);
      setRecipientInput('');
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    },
  });

  const handleEditConfig = (config: EmailConfiguration) => {
    setEditingConfig(config);
    setRecipientInput(config.recipients.join(', '));
  };

  const handleSaveConfig = () => {
    if (!editingConfig) return;

    // Parse and validate email addresses
    const recipients = recipientInput
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = recipients.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      alert(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      return;
    }

    updateConfigMutation.mutate({
      eventType: editingConfig.eventType,
      data: { recipients }
    });
  };

  const handleToggleEnabled = (config: EmailConfiguration) => {
    updateConfigMutation.mutate({
      eventType: config.eventType,
      data: { enabled: !config.enabled }
    });
  };

  const handleCancelEdit = () => {
    setEditingConfig(null);
    setRecipientInput('');
  };

  // Highlight the two new event types
  const isNewEventType = (eventType: string) => {
    return eventType === 'prepare_enrich_requested' || eventType === 'background_check_submitted';
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="px-4 sm:px-6 lg:px-8 py-8">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-3xl font-bold text-gray-900">Email Notifications</h1>
            <p className="mt-2 text-sm text-gray-700">
              Configure who receives email notifications for various portal events.
            </p>
            <p className="mt-3 text-sm">
              <Link
                to="/email-config/mbcb-report-submitted"
                className="font-medium text-primary-600 hover:text-primary-500"
              >
                MBCB report submitted notifications
              </Link>{' '}
              — separate settings for when planters submit monthly MBCB reports.
            </p>
          </div>
        </div>

        {showSuccessMessage && (
          <div className="mt-4 rounded-md bg-green-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-green-800">
                  Email configuration updated successfully!
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flow-root">
          <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
            <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
              <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
                <table className="min-w-full divide-y divide-gray-300">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                        Event Type
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Recipients
                      </th>
                      <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {configurations?.map((config) => {
                      const isEditing = editingConfig?.id === config.id;
                      const isNew = isNewEventType(config.eventType);
                      const label = eventTypeLabels[config.eventType];

                      return (
                        <tr key={config.id} className={isNew ? 'bg-blue-50' : ''}>
                          <td className="whitespace-normal py-4 pl-4 pr-3 text-sm sm:pl-6 sm:whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="flex items-center gap-2">
                                  <div className="font-medium text-gray-900">
                                    {label?.title || config.eventType}
                                  </div>
                                  {isNew && (
                                    <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-800">
                                      New
                                    </span>
                                  )}
                                </div>
                                <div className="text-gray-500">
                                  {label?.description || config.eventType}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-3 py-4 text-sm text-gray-500">
                            {isEditing ? (
                              <input
                                type="text"
                                value={recipientInput}
                                onChange={(e) => setRecipientInput(e.target.value)}
                                placeholder="email1@example.com, email2@example.com"
                                className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                              />
                            ) : config.recipients.length > 0 ? (
                              <div className="flex flex-wrap gap-1">
                                {config.recipients.map((email, idx) => (
                                  <span
                                    key={idx}
                                    className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800"
                                  >
                                    {email}
                                  </span>
                                ))}
                              </div>
                            ) : (
                              <span className="text-gray-400 italic">No recipients configured</span>
                            )}
                          </td>
                          <td className="whitespace-normal px-3 py-4 text-sm sm:whitespace-nowrap">
                            <button
                              onClick={() => handleToggleEnabled(config)}
                              disabled={updateConfigMutation.isPending}
                              className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${
                                config.enabled
                                  ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              } transition-colors`}
                            >
                              {config.enabled ? 'Enabled' : 'Disabled'}
                            </button>
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            {isEditing ? (
                              <div className="flex flex-col justify-end gap-2 sm:flex-row sm:items-center">
                                <button
                                  onClick={handleSaveConfig}
                                  disabled={updateConfigMutation.isPending}
                                  className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={handleCancelEdit}
                                  disabled={updateConfigMutation.isPending}
                                  className="text-gray-600 hover:text-gray-900 disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => handleEditConfig(config)}
                                className="text-primary-600 hover:text-primary-900"
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 rounded-lg bg-blue-50 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">About Email Notifications</h3>
              <div className="mt-2 text-sm text-blue-700">
                <p>
                  Configure email recipients for each event type. You can add multiple email addresses separated by commas.
                  Toggle notifications on/off for each event type. Only enabled events will send emails.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default EmailConfig;

